import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Loader2, Mail, Phone, MapPin } from 'lucide-react';
import { useState } from 'react';
import ContactItem from '../components/contactItem';
import ContactField from '../components/ContactField';
import { useSubmitContact } from '../functions/contactHook';

const BRAND_COLORS = {
  primary: '#0F2C59',
  secondary: '#1A4D8C',
  accent: '#3A83F1',
  text: '#2D3748'
};

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { mutate: submitContact, isLoading, isSuccess } = useSubmitContact();

  const contactItems = [
    { icon: <Mail className="w-5 h-5" />, title: 'Email', content: 'support@templatehub.com' },
    { icon: <Phone className="w-5 h-5" />, title: 'Phone', content: '+1 (555) 123-4567' },
    { icon: <MapPin className="w-5 h-5" />, title: 'Office', content: '123 Template Street, Web City' }
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Required field';
    if (!formData.email.trim()) newErrors.email = 'Required field';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.subject.trim()) newErrors.subject = 'Required field';
    if (!formData.message.trim()) newErrors.message = 'Required field';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    submitContact(formData);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="bg-white py-16">
      <div className="max-w-5xl mx-auto px-6">
        <motion.header className="text-center mb-16" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: BRAND_COLORS.primary }}>
            Strategic Partnership Inquiry
          </h1>
          <p className="mt-4 text-lg" style={{ color: BRAND_COLORS.text }}>
            Accelerate your digital transformation with our enterprise solutions
          </p>
        </motion.header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8 border-r-0 lg:border-r lg:pr-12" style={{ borderColor: BRAND_COLORS.accent + '30' }}>
            {contactItems.map((item, index) => (
              <ContactItem key={index} {...item} color={BRAND_COLORS.primary} />
            ))}
          </div>

          <motion.div className="bg-white p-8 rounded-xl shadow-sm" 
            style={{ border: `1px solid ${BRAND_COLORS.accent}20` }}>
            <AnimatePresence mode='wait'>
              {isSuccess ? (
                <motion.div className="text-center p-8 space-y-5" 
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <CheckCircle className="w-12 h-12 mx-auto" style={{ color: BRAND_COLORS.accent }} />
                  <h3 className="text-xl font-semibold" style={{ color: BRAND_COLORS.primary }}>
                    Engagement Received
                  </h3>
                  <p className="text-gray-600">Our executive team will contact you within 1 business day</p>
                </motion.div>
              ) : (
                <motion.form onSubmit={handleSubmit} className="space-y-6">
                  {['name', 'email', 'subject'].map(field => (
                    <ContactField
                      key={field}
                      field={field}
                      value={formData[field as keyof typeof formData]}
                      onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                      error={errors[field]}
                      accentColor={BRAND_COLORS.accent}
                    />
                  ))}
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: BRAND_COLORS.text }}>
                      Project Details
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 ${
                        errors.message ? 'border-red-300' : 'border-gray-200 focus:border-transparent'
                      }`}
                      style={{ 
                        color: BRAND_COLORS.text,
                        focusRingColor: BRAND_COLORS.accent,
                        borderColor: errors.message ? '#FCA5A5' : BRAND_COLORS.accent + '30'
                      }}
                      rows={4}
                    />
                    {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3.5 px-6 rounded-lg font-medium transition-all flex justify-center items-center gap-2"
                    style={{ 
                      backgroundColor: BRAND_COLORS.primary,
                      color: 'white',
                      hoverBg: BRAND_COLORS.secondary,
                      disabledOpacity: '0.7'
                    }}
                  >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Initiate Partnership'}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
}